import { v4 as uuid } from 'uuid';

import type { SupportedLanguage } from '../../../translate';
import { emptyText, text } from './epiHelpers';
import { orderSiblings } from './sectionTree';
import type {
  EpiComposition,
  EpiCustomSection,
  EpiDataSections,
  EpiDocumentType,
  EpiHeadingLevel,
  EpiIdmpLink,
  EpiPackagingBlock,
  EpiPredefinedEntry,
  EpiSchemaCompositionDef,
  EpiSchemaCustomSections,
  EpiSchemaSectionDef,
  EpiSchemaSectionEntry,
  EpiSchemaSections,
  EpiSection,
  EpiSectionNode,
  EpiText,
} from '../types/types';

import epiSchema from '../data/epi-schema.json';

// ============================================================================
// IDMP VISIBILITY (soft-delete filter)
// ============================================================================

/**
 * Drops wrappers marked `toBeDeleted`. They stay in FE state so the BE merge
 * removes them from the persisted array on save, so everything that reads an
 * idmp set for display or coverage has to filter through this first.
 */
export function visibleIdmp<T extends { toBeDeleted?: boolean }>(
  idmp: T[]
): T[] {
  return idmp.filter(r => !r.toBeDeleted);
}

// ============================================================================
// SCHEMA ACCESS
// ============================================================================

/**
 * Gets the composition schema definition for a given document type.
 * Resolves $ref internally.
 */
/** The top-level property names the ePI schema declares. */
export function getDocumentPropertyNames(): string[] {
  return Object.keys((epiSchema as any).properties ?? {});
}

export function getCompositionSchema(
  docType: EpiDocumentType
): EpiSchemaCompositionDef {
  const refMap: Record<EpiDocumentType, string> = {
    smpc: 'smpcComposition',
    annexII: 'annexIIComposition',
    labelling: 'labellingComposition',
    packageLeaflet: 'packageLeafletComposition',
  };
  const def = (epiSchema as any).$defs[refMap[docType]];
  return def as EpiSchemaCompositionDef;
}

/**
 * Normalizes a section entry: a repeatable key is declared as an array whose
 * `items` holds the ordinary object definition, so unwrapping it here lets
 * every other consumer keep working on the object def.
 */
export function resolveSectionDef(entry: EpiSchemaSectionEntry): {
  def: EpiSchemaSectionDef;
  repeatable: boolean;
} {
  return entry.type === 'array'
    ? { def: entry.items, repeatable: true }
    : { def: entry, repeatable: false };
}

/** Drops the instance index from a path segment ("blisterStrip[1]" -> "blisterStrip"). */
function stripIndex(pathSegment: string): string {
  return pathSegment.replace(/\[\d+\]$/, '');
}

/**
 * Gets ordered section keys from a schema sections object.
 * The key order in JSON Schema properties defines the canonical display order.
 */
export function getOrderedSectionKeys(
  schemaSections: Record<string, EpiSchemaSectionEntry>
): string[] {
  return Object.keys(schemaSections);
}

/**
 * Checks if a section key is mandatory in its parent's schema.
 */
export function isSectionMandatory(
  parentSectionsSchema: { required?: string[] },
  key: string
): boolean {
  return parentSectionsSchema.required?.includes(key) ?? false;
}

/**
 * Gets the default title from a schema section definition.
 */
export function getDefaultTitle(schemaDef: EpiSchemaSectionDef): EpiText {
  return { ...emptyText(), ...(schemaDef.properties?.title?.default ?? {}) };
}

/**
 * Gets the default content from a schema section definition.
 */
export function getDefaultContent(schemaDef: EpiSchemaSectionDef): EpiText {
  return { ...emptyText(), ...(schemaDef.properties?.content?.default ?? {}) };
}

/**
 * A QRD heading the author has something to resolve in.
 *
 * The convention fixes the headings — *"Must respect the current template. No
 * additional numbering should be created"* (EMA/62470/2007 rev. 8, and the
 * ALIMS translation) — so a title is not the author's to reword. The exception
 * is the bracketing convention, which applies to headings as much as to body
 * text: `<text>` is chosen or deleted, `{text}` is filled in, and a standalone
 * `X` stands for the invented name. 29 of the 127 declared titles carry one of
 * those; the other 98 have nothing to decide.
 *
 * Read from the **template**, never from the stored title: once an author
 * resolves `<uzmete>` the notation is gone, and testing the current value would
 * lock the field after the first edit.
 */
const RESOLVABLE = /[<˂{]|\bX\b/;

export function titleNeedsResolving(
  schemaDef: EpiSchemaSectionDef,
  lang: SupportedLanguage
): boolean {
  return RESOLVABLE.test(schemaDef.properties?.title?.default?.[lang] ?? '');
}

/**
 * Whether a section takes custom subsections — true exactly when the schema
 * declares a `customSections` array on it. The BE normalizes every object
 * subschema to `additionalProperties: false` when a Model is registered
 * (`JsonSchemaNormalizer`), so declaring the array *is* the permission: a
 * section without it rejects one. Nothing else needs to say so.
 *
 * This mirrors RMS `BLANK_SUBSECTION_ALLOWED`: 50 of the 131 declared
 * sections, and the SmPC custom H4 itself, which hosts custom H5s.
 */
export function allowsCustomSections(schemaDef: {
  properties?: { customSections?: unknown };
}): boolean {
  return schemaDef.properties?.customSections !== undefined;
}

/**
 * Whether a block carries a `{NATURE/TYPE}` descriptor. The packaging blocks
 * do; the patient card does not, and the server rejects one written onto it.
 */
export function takesNatureType(schemaDef: EpiSchemaSectionDef): boolean {
  return schemaDef.properties?.natureType !== undefined;
}

// ============================================================================
// SECTION TREE BUILDING (schema + data → EpiSectionNode[])
// ============================================================================

/**
 * Builds the UI section tree by walking the schema and matching with instance data.
 * Only sections present in the data are included in the tree.
 * Optional sections not yet added are excluded but available via getAvailableOptionalSections.
 */
export function buildSectionTree(
  lang: SupportedLanguage,
  schemaSections: Record<string, EpiSchemaSectionEntry>,
  parentSectionsSchema: { required?: string[] },
  dataSections: EpiDataSections | undefined,
  parentPath: string
): EpiSectionNode[] {
  const nodes: EpiSectionNode[] = [];
  const blockNodes: Array<{ order: number; node: EpiSectionNode }> = [];

  for (const key of getOrderedSectionKeys(schemaSections)) {
    const dataValue = dataSections?.[key];
    if (!dataValue) continue; // optional section not yet added

    const { def, repeatable } = resolveSectionDef(schemaSections[key]);
    const path = parentPath ? `${parentPath}.${key}` : key;
    const mandatory = isSectionMandatory(parentSectionsSchema, key);

    if (repeatable) {
      // A repeatable key holds an array of blocks, each its own tree node
      // addressed by index. Soft-deleted blocks keep their slot so the paths
      // of the surviving ones stay valid.
      const blocks = Array.isArray(dataValue) ? dataValue : [];
      blocks.forEach((block, index) => {
        if (block.toBeDeleted) return;
        blockNodes.push({
          order: block.order ?? 0,
          node: buildSectionNode(
            lang,
            def,
            block,
            key,
            `${path}[${index}]`,
            mandatory,
            true
          ),
        });
      });
      continue;
    }

    nodes.push(
      buildSectionNode(
        lang,
        def,
        dataValue as EpiSection,
        key,
        path,
        mandatory,
        false
      )
    );
  }

  // Blocks print after the plain sections, by `order` across every repeatable
  // key — the export's rule, so the menu shows packaging in print order.
  blockNodes.sort((a, b) => a.order - b.order);
  return [...nodes, ...blockNodes.map(({ node }) => node)];
}

/** Builds one tree node (plus its fixed and custom children) from schema + data. */
function buildSectionNode(
  lang: SupportedLanguage,
  schemaDef: EpiSchemaSectionDef,
  dataSection: EpiSection,
  key: string,
  path: string,
  isMandatory: boolean,
  repeatable: boolean
): EpiSectionNode {
  const childSchema = schemaDef.properties?.sections;
  const children = childSchema
    ? buildSectionTree(
        lang,
        childSchema.properties,
        childSchema,
        dataSection.sections,
        `${path}.sections`
      )
    : [];

  const customChildren = buildCustomNodes(
    lang,
    schemaDef.properties?.customSections,
    dataSection.customSections,
    path
  );
  const placeById = new Map(
    (dataSection.customSections ?? []).map(cs => [cs.id, cs])
  );

  return {
    path,
    key,
    title: text(dataSection.title, lang),
    content: text(dataSection.content, lang),
    isCustom: false,
    isMandatory,
    allowCustom: allowsCustomSections(schemaDef),
    titleReadonly: !titleNeedsResolving(schemaDef, lang),
    titleUnresolved:
      titleNeedsResolving(schemaDef, lang) &&
      RESOLVABLE.test(text(dataSection.title, lang)),
    repeatable,
    natureType:
      text((dataSection as EpiPackagingBlock).natureType, lang) || undefined,
    hasNatureType: repeatable && takesNatureType(schemaDef),
    sequenceOrder: dataSection.sequenceOrder,
    unmatchedBelow: hasUnmatched([...children, ...customChildren]),
    children: orderSiblings(
      children,
      customChildren,
      node => node.sequenceOrder ?? 0,
      node => placeById.get(node.key)!
    ),
  };
}

function hasUnmatched(nodes: EpiSectionNode[]): boolean {
  return nodes.some(node => node.unmatched || node.unmatchedBelow);
}

/**
 * Builds the full section tree for a composition.
 */
export function buildCompositionSectionTree(
  docType: EpiDocumentType,
  composition: EpiComposition,
  lang: SupportedLanguage
): EpiSectionNode[] {
  const schema = getCompositionSchema(docType);
  const children = buildSectionTree(
    lang,
    schema.properties.sections.properties,
    schema.properties.sections,
    composition.sections,
    'sections'
  );

  // Wrap in a root node representing the document root section
  const rootNode: EpiSectionNode = {
    path: 'root',
    key: 'root',
    title: text(composition.rootTitle, lang),
    content: text(composition.rootContent, lang),
    isCustom: false,
    isMandatory: true,
    allowCustom: false,
    titleReadonly: true,
    repeatable: false,
    unmatchedBelow: hasUnmatched(children),
    children,
  };

  return [rootNode];
}

// ============================================================================
// AVAILABLE (NOT YET ADDED) OPTIONAL SECTIONS
// ============================================================================

/**
 * Returns optional sections defined in the schema but not yet present in the data.
 * These are sections the user can choose to add.
 */
export function getAvailableOptionalSections(
  schemaSections: Record<string, EpiSchemaSectionEntry>,
  parentSectionsSchema: { required?: string[] },
  dataSections: Record<string, unknown> | undefined,
  lang: SupportedLanguage
): { key: string; title: string; repeatable: boolean }[] {
  return getOrderedSectionKeys(schemaSections)
    .filter(key => {
      // A repeatable key can always take another instance — even when it is
      // required and already present (a second packaging block).
      if (resolveSectionDef(schemaSections[key]).repeatable) return true;
      return (
        !isSectionMandatory(parentSectionsSchema, key) && !dataSections?.[key]
      );
    })
    .map(key => {
      const { def, repeatable } = resolveSectionDef(schemaSections[key]);
      return { key, title: text(getDefaultTitle(def), lang), repeatable };
    });
}

// ============================================================================
// BUILDING EMPTY DATA FROM SCHEMA
// ============================================================================

/**
 * Builds an empty section from a schema definition, populating only mandatory children.
 */
export function buildEmptySection(schemaDef: EpiSchemaSectionDef): EpiSection {
  const section: EpiSection = {
    title: getDefaultTitle(schemaDef),
    content: getDefaultContent(schemaDef),
    rmsCode: schemaDef.properties?.rmsCode?.default ?? '',
    level: schemaDef.properties?.level?.default ?? 'H1',
    sequenceOrder: schemaDef.properties?.sequenceOrder?.default ?? 0,
  };

  const childSchema = schemaDef.properties?.sections;
  if (childSchema) {
    const requiredKeys = childSchema.required ?? [];
    const sections: EpiDataSections = {};

    for (const key of getOrderedSectionKeys(childSchema.properties)) {
      if (requiredKeys.includes(key)) {
        sections[key] = buildEmptySectionEntry(childSchema.properties[key]);
      }
    }

    if (Object.keys(sections).length > 0) {
      section.sections = sections;
    }
  }

  if (allowsCustomSections(schemaDef)) {
    section.customSections = [];
  }

  return section;
}

/**
 * Builds the value stored under one section key: a single section, or — for a
 * repeatable key — a one-element array holding its first block.
 */
export function buildEmptySectionEntry(
  entry: EpiSchemaSectionEntry
): EpiSection | EpiPackagingBlock[] {
  const { def, repeatable } = resolveSectionDef(entry);
  return repeatable ? [buildEmptyPackagingBlock(def)] : buildEmptySection(def);
}

/**
 * Builds one instance of a repeatable packaging block. The block carries its
 * own id (BE merges array items by id) and, when the user picked one, the
 * nature/type that distinguishes it from sibling instances.
 */
export function buildEmptyPackagingBlock(
  schemaDef: EpiSchemaSectionDef,
  natureType?: EpiText,
  order = 0
): EpiPackagingBlock {
  // Falls back to the schema default, which is what labels the block seeded
  // automatically with a new composition — nobody is prompted for that one.
  const resolved: EpiText | undefined =
    natureType ?? schemaDef.properties?.natureType?.default;

  return {
    id: uuid(),
    ...(resolved ? { natureType: resolved } : {}),
    order,
    ...buildEmptySection(schemaDef),
  };
}

/**
 * Builds a new empty composition from a schema definition, with only mandatory sections.
 */
/**
 * What a document of this type is called, for chrome that names one before it
 * exists. The English default, because the interface is English throughout.
 */
export function documentTypeName(docType: EpiDocumentType): string {
  const title = (getCompositionSchema(docType).properties as any).title;
  return title?.default?.en ?? '';
}

export function buildEmptyComposition(
  docType: EpiDocumentType,
  medicinalProducts: EpiIdmpLink[],
  title?: EpiText,
  order = 0
): EpiComposition {
  const schema = getCompositionSchema(docType);
  const requiredKeys = schema.properties.sections.required ?? [];
  const schemaSections = schema.properties.sections.properties;

  const sections: EpiDataSections = {};
  for (const key of getOrderedSectionKeys(schemaSections)) {
    if (requiredKeys.includes(key)) {
      sections[key] = buildEmptySectionEntry(schemaSections[key]);
    }
  }

  const rootTitle = {
    ...emptyText(),
    ...(schema.properties.rootTitle?.default ?? {}),
  };
  const rootContent = {
    ...emptyText(),
    ...(schema.properties.rootContent?.default ?? {}),
  };

  return {
    id: uuid(),
    // Every other default here comes from the schema; so does this one. The
    // name of a document type is part of the model, not of the code that
    // happens to create one.
    title: title ?? {
      ...emptyText(),
      ...((schema.properties as any).title?.default ?? {}),
    },
    rootTitle,
    rootContent,
    medicinalProducts,
    rmsCode: (schema.properties as any).rmsCode?.default ?? '',
    level: (schema.properties as any).level?.default ?? 'H1',
    sequenceOrder: (schema.properties as any).sequenceOrder?.default ?? 0,
    order,
    sections,
  };
}

// ============================================================================
// SECTION NODE LOOKUP
// ============================================================================

/**
 * Finds a section node in a tree by its path.
 */
export function findSectionNode(
  nodes: EpiSectionNode[],
  path: string
): EpiSectionNode | null {
  for (const node of nodes) {
    if (node.path === path) return node;
    const found = findSectionNode(node.children, path);
    if (found) return found;
  }
  return null;
}

/**
 * The chain of nodes from the tree root down to `path`, inclusive. Empty when
 * the path is not in the tree.
 */
export function getSectionAncestry(
  nodes: EpiSectionNode[],
  path: string
): EpiSectionNode[] {
  for (const node of nodes) {
    if (node.path === path) return [node];
    const below = getSectionAncestry(node.children, path);
    if (below.length) return [node, ...below];
  }
  return [];
}

/**
 * Tree nodes for the custom sections under `hostPath`, and theirs in turn —
 * an SmPC custom H4 hosts custom H5s. Whether a node may take more comes from
 * its item schema, as it does for declared sections.
 */
function buildCustomNodes(
  lang: SupportedLanguage,
  schema: EpiSchemaCustomSections | undefined,
  customSections: EpiCustomSection[] | undefined,
  hostPath: string
): EpiSectionNode[] {
  const itemSchema = schema?.items ?? {};
  // Keep the original array index in the path so soft-deleted entries don't
  // shift the addresses of the visible ones. Ordered here for the nested ones,
  // which have no declared siblings; a host places its own among its declared
  // children.
  const visible = (customSections ?? [])
    .map((cs, originalIndex) => ({ cs, originalIndex }))
    .filter(({ cs }) => !cs.toBeDeleted);
  return orderSiblings(
    [],
    visible,
    () => 0,
    ({ cs }) => cs
  ).map(({ cs, originalIndex }) => {
    const path = `${hostPath}.customSections[${originalIndex}]`;
    const children = buildCustomNodes(
      lang,
      itemSchema.properties?.customSections,
      cs.customSections,
      path
    );
    return {
      path,
      key: cs.id,
      title: text(cs.title, lang),
      content: text(cs.content, lang),
      isCustom: true,
      isMandatory: false,
      allowCustom: allowsCustomSections(itemSchema),
      // A predefined title is the entry's, in both languages; a free or
      // unmatched one is the author's.
      titleReadonly: !!cs.predefinedKey,
      repeatable: false,
      predefinedKey: cs.predefinedKey,
      unmatched: !!cs.unmatched,
      unmatchedBelow: hasUnmatched(children),
      children,
    };
  });
}

/**
 * The predefined entries a section's custom sections may be: its own array's
 * for a declared section, its entry's `children` for a predefined one. A free
 * or unmatched custom section has none, even where the schema would accept a
 * child key.
 */
export function getPredefinedEntries(
  docType: EpiDocumentType,
  node: Pick<EpiSectionNode, 'path' | 'isCustom' | 'predefinedKey'>
): EpiPredefinedEntry[] {
  if (!node.isCustom) {
    return (
      getSchemaSectionAtPath(docType, node.path)?.properties?.customSections?.[
        'x-predefined'
      ] ?? []
    );
  }
  if (!node.predefinedKey) return [];
  const hostPath = node.path.replace(/\.customSections\[\d+\]$/, '');
  const hostEntries =
    getSchemaSectionAtPath(docType, hostPath)?.properties?.customSections?.[
      'x-predefined'
    ] ?? [];
  return (
    hostEntries.find(entry => entry.key === node.predefinedKey)?.children ?? []
  );
}

/**
 * Resolves a schema section definition by following a dot-separated path.
 * e.g. "sections.clinicalParticulars.sections.posology"
 */
export function getSchemaSectionAtPath(
  docType: EpiDocumentType,
  path: string
): EpiSchemaSectionDef | undefined {
  const entry = getSchemaEntryAtPath(docType, path);
  return entry ? resolveSectionDef(entry).def : undefined;
}

/**
 * The RMS identity a custom section takes under `parentPath`: one heading level
 * below its host, and the reserved "blank sub-section" term code that goes with
 * that level (H3 in the PL, H4/H5 in the SmPC). Both are read from the schema so
 * the rule has a single source.
 */
export function getCustomSectionDefaults(
  docType: EpiDocumentType,
  parentPath: string
): { level: EpiHeadingLevel; rmsCode: string } {
  const def =
    parentPath === 'root'
      ? (getCompositionSchema(docType) as unknown as EpiSchemaSectionDef)
      : getSchemaSectionAtPath(docType, parentPath);
  const props = def?.properties?.customSections?.items?.properties;
  return {
    level: props?.level?.default ?? 'H3',
    rmsCode: props?.rmsCode?.default ?? '',
  };
}

/**
 * Same lookup as {@link getSchemaSectionAtPath}, but returns the raw entry so
 * the caller can tell a repeatable key from a plain one.
 */
export function getSchemaEntryAtPath(
  docType: EpiDocumentType,
  path: string
): EpiSchemaSectionEntry | undefined {
  const schema = getCompositionSchema(docType);

  // Root node represents the composition itself — return a virtual def wrapping top-level sections
  if (path === 'root') {
    return {
      type: 'object',
      properties: { sections: schema.properties.sections },
    } as any;
  }

  const parts = path.split('.');

  let current: any = { properties: { sections: schema.properties.sections } };

  for (const part of parts) {
    if (part === 'sections') {
      current = current.properties?.sections;
    } else if (part.startsWith('customSections')) {
      // A custom section's definition is its array's item, shared by every
      // instance. It declares `customSections` of its own where it may host
      // more — the SmPC custom H4.
      current = current?.properties?.customSections?.items;
    } else {
      // A repeatable instance segment looks like "blisterStrip[1]" — the index
      // picks the data instance, the schema definition is shared by all of them.
      const entry = current?.properties?.[stripIndex(part)];
      // Unwrap so the next iteration can walk into `properties.sections`, but
      // leave the last segment's entry intact for the caller.
      current =
        entry && part === parts[parts.length - 1]
          ? entry
          : entry && resolveSectionDef(entry).def;
    }
    if (!current) return undefined;
  }

  return current as EpiSchemaSectionEntry;
}

/**
 * Gets the parent sections schema for a given path (needed for required checks).
 */
export function getParentSectionsSchema(
  docType: EpiDocumentType,
  path: string
): EpiSchemaSections | undefined {
  const schema = getCompositionSchema(docType);
  const parts = path.split('.');

  // path looks like "sections.foo.sections.bar" — we need the "sections.foo.sections" part
  if (parts.length < 2) {
    return schema.properties.sections as any;
  }

  const parentPath = parts.slice(0, -1).join('.');
  const parentParts = parentPath.split('.');

  let current: any = { properties: { sections: schema.properties.sections } };

  for (const part of parentParts) {
    if (part === 'sections') {
      current = current.properties?.sections;
    } else {
      const entry = current?.properties?.[stripIndex(part)];
      current = entry && resolveSectionDef(entry).def;
    }
    if (!current) return undefined;
  }

  return current;
}
